import {
  ENTITY_CLASS,
  MeasurementQueryFilters,
  QueryOptions,
  streamError,
  toUniversalError,
} from "@packages/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { fetchData } from "./functions";
import { QueryKey } from "./query";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

// =============================================================
// 一覧
// =============================================================

export async function listMeasurements(filters?: MeasurementQueryFilters, options?: QueryOptions) {
  const { output } = await fetchData({
    type: "Entities::Measurements",
    input: { filters, options },
  });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function createMeasurementsQueryKey(...[queries, options]: ReadonlyArray<any>) {
  return QueryKey(ENTITY_CLASS.MEASUREMENT + "List", queries, options);
}

export function removeMeasurementsCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: createMeasurementsQueryKey().slice(0, 1) });
}

export function useListedMeasurements(
  filters?: MeasurementQueryFilters,
  options?: QueryOptions & {
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled?: boolean;
  },
) {
  const { data, error, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: createMeasurementsQueryKey(filters, options),
    async queryFn(context) {
      return listMeasurements(filters, {
        ...options,
        nextPageToken: context.pageParam.nextPageToken,
      });
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  useEffect(
    function () {
      if (error) {
        streamError(error);
      }
    },
    [error],
  );

  return useMemo(
    function () {
      return {
        error: error ? toUniversalError(error) : null,
        fetchNextPage,
        loaded: !hasNextPage,
        loading: isLoading,
        pages: data?.pages ?? [],
      };
    },
    [data?.pages, error, fetchNextPage, hasNextPage, isLoading],
  );
}
